(() => {
  'use strict';

  // Tier thresholds — UI display only.
  // Recharge applies discounts as: plan 5% first, then tiered % on top (compounded).
  // tiered = the additional % Recharge's Tiered Discount adds on the already-discounted price.
  const PLAN_DISCOUNT = 0; // no flat plan discount — all discounts handled by Recharge tiered rules
  const TIERS = [
    { min: 1, max: 2,        discount: 5,  tiered: 5  },
    { min: 3, max: 5,        discount: 10, tiered: 10 },
    { min: 6, max: Infinity, discount: 15, tiered: 15 },
  ];

  // ── Plan selector (Subscribe & Save vs One-Time Purchase) ────────────
  const PlanSelector = {
    el:       null,
    selected: 'subscribe',

    init(el) {
      this.el = el;
      el.querySelectorAll('.js-plan-btn').forEach(btn => {
        btn.addEventListener('click', () => this.select(btn.dataset.plan));
      });
      el.querySelector('.js-sub-frequency')?.addEventListener('click', e => e.stopPropagation());
    },

    select(plan) {
      this.selected = plan;
      this.el.querySelectorAll('.js-plan-btn').forEach(btn => {
        const isActive = btn.dataset.plan === plan;
        btn.classList.toggle('border-blue-400', isActive);
        btn.classList.toggle('border-gray-200',  !isActive);
        btn.classList.toggle('bg-blue-50', isActive && btn.dataset.plan === 'subscribe');
        btn.classList.toggle('bg-white',   !isActive && btn.dataset.plan === 'subscribe');
        btn.setAttribute('aria-pressed', isActive);

        const iconWrap = btn.querySelector('.js-plan-icon');
        if (iconWrap) {
          iconWrap.innerHTML = isActive
            ? `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" fill="#3f9cd2"/><circle cx="10" cy="10" r="4" fill="white"/></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="#d1d5db" stroke-width="1.5"/></svg>`;
        }

        if (btn.dataset.plan === 'subscribe') {
          btn.querySelector('.js-plan-freq-wrap')?.classList.toggle('hidden', !isActive);
        }
      });

      SubBuybox.updateUI();
    },
  };

  // ── Tab switcher (Exchange / Spare variant cards) ─────────────────────
  const TabSwitcher = {
    el: null,

    init(el) {
      this.el = el;
      // No plan cards rendered (no selling plans, or B2B customer) — one-time purchase only
      if (!el.querySelector('.js-plan-btn')) this.selected = 'otp';

      el.querySelectorAll('.js-variant-tab').forEach(btn => {
        btn.addEventListener('click', () => this.switchTo(btn.dataset.tab));
      });
    },

    switchTo(tab) {
      this.el.querySelectorAll('.js-variant-tab').forEach(btn => {
        const isActive = btn.dataset.tab === tab;
        btn.classList.toggle('border-blue-400', isActive);
        btn.classList.toggle('border-gray-200', !isActive);
        btn.setAttribute('aria-pressed', isActive);

        const iconWrap = btn.querySelector('.js-variant-tab-icon');
        if (iconWrap) {
          iconWrap.innerHTML = isActive
            ? `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" fill="#3f9cd2"/><path d="M6 10l3 3 5-5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="#d1d5db" stroke-width="1.5"/></svg>`;
        }
      });

      this.el.querySelectorAll('.js-tab-content').forEach(panel => {
        const isActive = panel.dataset.content === tab;
        panel.classList.toggle('hidden', !isActive);
        panel.classList.toggle('flex',    isActive);
      });

      if (window._subBuyboxZipValidated) {
        // Keep both check-avail buttons hidden; show ATC only on the active tab
        this.el.querySelector('.js-sub-check-avail')?.classList.add('hidden');
        this.el.querySelector('.js-spare-check-avail')?.classList.add('hidden');
        if (tab === 'spare') {
          const spareBtn = this.el.querySelector('.js-spare-add-to-cart');
          spareBtn?.classList.remove('hidden');
          spareBtn?.classList.add('flex');
          const exchBtn = this.el.querySelector('.js-sub-add-to-cart');
          exchBtn?.classList.add('hidden');
          exchBtn?.classList.remove('flex');
        } else {
          const exchBtn = this.el.querySelector('.js-sub-add-to-cart');
          exchBtn?.classList.remove('hidden');
          exchBtn?.classList.add('flex');
          const spareBtn = this.el.querySelector('.js-spare-add-to-cart');
          spareBtn?.classList.add('hidden');
          spareBtn?.classList.remove('flex');
        }
      }
    },
  };

  // ── Spare tank — subscription if plans exist, OTP otherwise ──────────
  const SpareBuybox = {
    el:            null,
    qty:           1,
    variantId:     0,
    basePrice:     0,
    sellingPlanId: 0,
    planSelected:  'subscribe',
    hasPlans:      false,
    existingSubQty: 0,

    init(el) {
      this.el        = el;
      this.variantId = parseInt(el.dataset.spareVariantId, 10);
      this.basePrice = parseInt(el.dataset.spareBasePrice, 10);
      if (!this.variantId) return;

      this.hasPlans = !!this.qs('.js-spare-plan-btn');
      if (this.hasPlans) {
        this.sellingPlanId = parseInt(this.qs('.js-spare-frequency')?.value || '0', 10);
        this.planSelected  = 'subscribe';
      }

      this.bindEvents();
      this.updateUI();
    },

    qs(sel)    { return this.el.querySelector(sel); },
    qsAll(sel) { return this.el.querySelectorAll(sel); },

    bindEvents() {
      this.qs('.js-spare-qty-minus')?.addEventListener('click', () => this.setQty(this.qty - 1));
      this.qs('.js-spare-qty-plus')?.addEventListener('click',  () => this.setQty(this.qty + 1));
      this.qs('.js-spare-add-to-cart')?.addEventListener('click', () => this.addToCart());
      this.qs('.js-spare-check-avail')?.addEventListener('click', () => SubBuybox.openDeliveryDrawer());

      this.qsAll('.js-spare-plan-btn').forEach(btn => {
        btn.addEventListener('click', () => this.selectPlan(btn.dataset.plan));
      });

      this.qs('.js-spare-frequency')?.addEventListener('click', e => e.stopPropagation());
      this.qs('.js-spare-frequency')?.addEventListener('change', e => {
        this.sellingPlanId = parseInt(e.target.value, 10);
        const display = this.qs('.js-spare-freq-display');
        if (display) display.textContent = e.target.options[e.target.selectedIndex]?.text || '';
      });
    },

    selectPlan(plan) {
      this.planSelected = plan;
      this.qsAll('.js-spare-plan-btn').forEach(btn => {
        const isActive = btn.dataset.plan === plan;
        btn.classList.toggle('border-blue-400', isActive);
        btn.classList.toggle('border-gray-200',  !isActive);
        btn.classList.toggle('bg-blue-50', isActive && btn.dataset.plan === 'subscribe');
        btn.classList.toggle('bg-white',   !isActive && btn.dataset.plan === 'subscribe');
        btn.setAttribute('aria-pressed', isActive);

        const iconWrap = btn.querySelector('.js-spare-plan-icon');
        if (iconWrap) {
          iconWrap.innerHTML = isActive
            ? `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" fill="#3f9cd2"/><circle cx="10" cy="10" r="4" fill="white"/></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="#d1d5db" stroke-width="1.5"/></svg>`;
        }

        if (btn.dataset.plan === 'subscribe') {
          btn.querySelector('.js-spare-plan-freq-wrap')?.classList.toggle('hidden', !isActive);
        }
      });
      this.updateUI();
    },

    setQty(n) {
      this.qty = Math.max(1, n);
      this.updateUI();
    },

    getTier() {
      const totalQty = this.qty + this.existingSubQty;
      return TIERS.find(t => totalQty >= t.min && totalQty <= t.max) ?? TIERS[0];
    },

    calcUnitCents(tier) {
      const planPrice  = Math.round(this.basePrice * (1 - PLAN_DISCOUNT));
      const tieredDisc = Math.floor(planPrice * tier.tiered / 100);
      return planPrice - tieredDisc;
    },

    money(cents) {
      const val = (cents / 100).toFixed(2);
      return '$' + (val.endsWith('.00') ? val.slice(0, -3) : val);
    },

    updateUI() {
      const isSubscribe = this.hasPlans && this.planSelected !== 'otp';
      const tier        = isSubscribe ? this.getTier() : { min: 1, max: 2, discount: 0, tiered: 0 };
      const unitCents   = isSubscribe ? this.calcUnitCents(tier) : this.basePrice;

      this.qs('.js-spare-tier-wrap')?.classList.toggle('hidden', !isSubscribe);

      const set = (sel, val) => { const el = this.qs(sel); if (el) el.textContent = val; };
      set('.js-spare-qty',        this.qty);
      set('.js-spare-unit-price', this.money(unitCents));
      set('.js-spare-btn-total',  `— ${this.money(unitCents * this.qty)}`);

      const badge = this.qs('.js-spare-discount-badge');
      if (badge) {
        badge.textContent = `${tier.discount}% off`;
        badge.classList.toggle('hidden', !isSubscribe || tier.discount === 0);
      }

      const compareEl = this.qs('.js-spare-compare-price');
      if (compareEl) {
        compareEl.textContent = this.money(this.basePrice);
        compareEl.classList.toggle('hidden', !isSubscribe || tier.discount === 0);
      }

      const subTier      = this.getTier();
      const subUnitCents = this.calcUnitCents(subTier);
      const planSubPrice = this.qs('.js-spare-plan-sub-price');
      const planCompare  = this.qs('.js-spare-plan-compare');
      const planOtpPrice = this.qs('.js-spare-plan-otp-price');
      if (planSubPrice) planSubPrice.textContent = this.money(subUnitCents);
      if (planCompare) {
        planCompare.textContent = this.money(this.basePrice);
        planCompare.classList.toggle('hidden', subTier.discount === 0);
      }
      if (planOtpPrice) planOtpPrice.textContent = this.money(this.basePrice);

      if (this.qs('.js-spare-qty-minus')) this.qs('.js-spare-qty-minus').disabled = this.qty <= 1;

      const spareTotalQty = this.qty + this.existingSubQty;
      const pills  = this.qsAll('.js-spare-tier[data-min]');
      const labels = this.qsAll('.js-spare-tier-label');
      pills.forEach((pill, i) => {
        const isActive = isSubscribe && spareTotalQty >= parseInt(pill.dataset.min, 10) && spareTotalQty <= parseInt(pill.dataset.max, 10);
        pill.style.backgroundColor = isActive ? '#3f9cd2' : '#cce4f6';
        pill.style.color           = isActive ? '#ffffff' : '#3f9cd2';
        if (labels[i]) {
          labels[i].classList.toggle('font-bold',   isActive);
          labels[i].classList.toggle('font-normal', !isActive);
        }
      });

      const fill1 = this.qs('.js-spare-tier-fill-1');
      const fill2 = this.qs('.js-spare-tier-fill-2');
      let pct1 = 0, pct2 = 0;
      if (isSubscribe) {
        if (tier.min === 1)      { pct1 = ((spareTotalQty - 1) / 2) * 100; }
        else if (tier.min === 3) { pct1 = 100; pct2 = ((spareTotalQty - 3) / 3) * 100; }
        else                     { pct1 = 100; pct2 = 100; }
      }
      if (fill1) fill1.style.width = `${Math.max(Math.min(pct1, 100), isSubscribe ? 10 : 0)}%`;
      if (fill2) fill2.style.width = `${Math.min(pct2, 100)}%`;
    },

    async addToCart() {
      const btn    = this.qs('.js-spare-add-to-cart');
      const label  = this.qs('.js-spare-btn-text');
      const total  = this.qs('.js-spare-btn-total');
      const loader = this.qs('.js-spare-btn-loader');

      if (!btn || btn.disabled) return;
      btn.disabled = true;
      label?.classList.add('hidden');
      total?.classList.add('hidden');
      loader?.classList.remove('hidden');

      try {
        const item = { id: this.variantId, quantity: this.qty };
        if (this.hasPlans && this.planSelected !== 'otp') item.selling_plan = this.sellingPlanId;

        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description ?? 'Add to cart failed');

        // Re-sync existingSubQty so tier bar reflects the newly added items
        await SubBuybox.fetchExistingSubQty();
        SubBuybox.updateUI();
        this.updateUI();
      } catch (err) {
        console.error('[SpareBuybox]', err.message);
      } finally {
        btn.disabled = false;
        label?.classList.remove('hidden');
        total?.classList.remove('hidden');
        loader?.classList.add('hidden');
      }
    },
  };

  // ── Exchange subscription buybox ──────────────────────────────────────
  const SubBuybox = {
    el:            null,
    qty:           1,
    sellingPlanId: 0,
    basePrice:     0,
    variantId:     0,
    zipValidated:  false,
    existingSubQty: 0,

    async init() {
      this.el = document.querySelector('.js-sub-buybox');
      if (!this.el) return;

      this.basePrice     = parseInt(this.el.dataset.basePrice, 10);
      this.variantId     = parseInt(this.el.dataset.variantId, 10);
      this.sellingPlanId = parseInt(this.el.dataset.defaultPlan, 10);

      await this.fetchExistingSubQty();

      PlanSelector.init(this.el);
      if (this.el.querySelector('.js-variant-tab')) TabSwitcher.init(this.el);
      SpareBuybox.init(this.el);

      this.bindEvents();
      this.updateUI();
      this.bindCartSync();

      // If selling plans haven't synced to Liquid yet, fetch from product JSON
      const select = this.qs('.js-sub-frequency');
      if (select && select.options.length === 0) this.fetchAndPopulatePlans();
    },

    async fetchExistingSubQty() {
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const qty = (cart.items || [])
          .filter(item => item.selling_plan_allocation)
          .reduce((sum, item) => sum + item.quantity, 0);
        this.existingSubQty        = qty;
        SpareBuybox.existingSubQty = qty;
        // Keep header cart count in sync
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count } }));
      } catch (err) {
        console.warn('[SubBuybox] Cart fetch for tier calc failed:', err.message);
      }
    },

    bindCartSync() {
      let syncing = false;
      const resync = async () => {
        if (syncing) return;
        syncing = true;
        try {
          await this.fetchExistingSubQty();
          this.updateUI();
          SpareBuybox.updateUI();
        } finally {
          syncing = false;
        }
      };

      // Intercept fetch to catch Recharge's /cart/change.js and /cart/update.js
      // NOTE: we intentionally do NOT listen to cart:updated here — fetchExistingSubQty
      // dispatches cart:updated to keep the header count in sync, and listening to it
      // here would cause a second resync → second cart:updated → drawer opens twice.
      const origFetch = window.fetch;
      window.fetch = async function(...args) {
        const url = (args[0] instanceof Request ? args[0].url : String(args[0] || ''));
        const response = await origFetch.apply(this, args);
        if (/\/cart\/(change|update)(\.js)?/.test(url)) {
          setTimeout(resync, 50);
        }
        return response;
      };
    },

    qs(sel)    { return this.el.querySelector(sel); },
    qsAll(sel) { return this.el.querySelectorAll(sel); },

    bindEvents() {
      this.qs('.js-sub-qty-minus')?.addEventListener('click', () => this.setQty(this.qty - 1));
      this.qs('.js-sub-qty-plus')?.addEventListener('click',  () => this.setQty(this.qty + 1));

      // Frequency change: update plan ID and visible display text
      this.qs('.js-sub-frequency')?.addEventListener('change', e => {
        this.sellingPlanId = parseInt(e.target.value, 10);
        const display = this.qs('.js-freq-display');
        if (display) display.textContent = e.target.options[e.target.selectedIndex]?.text || '';
      });

      this.qs('.js-sub-check-avail')?.addEventListener('click', () => this.openDeliveryDrawer());
      this.qs('.js-sub-add-to-cart')?.addEventListener('click', () => this.addToCart());
      this.qs('.js-sub-read-more')?.addEventListener('click',   () => this.toggleDescription());
      this.qs('.js-edit-delivery')?.addEventListener('click',   () => this.resetDeliverability());

      this.watchDeliveringLine();
    },

    openDeliveryDrawer() {
      // Prefer the shared drawer module so the focus trap / keyboard handling runs.
      if (window.deliveryDrawer?.openDeliveryOverlay) {
        window.deliveryDrawer.openDeliveryOverlay();
        return;
      }

      // Fallback: no shared module available, toggle classes directly.
      const overlay = document.querySelector('.js-delivery-overlay');
      const drawer  = document.querySelector('.js-delivery-drawer');
      overlay?.classList.add('js-open-delivery-overlay');
      drawer?.classList.add('js-open-delivery-overlay');
      document.body.style.overflow = 'hidden';
    },

    watchDeliveringLine() {
      const line = this.qs('.js-deliveringzip');
      if (!line) return;
      new MutationObserver(() => {
        if (line.classList.contains('is-delivarable') && !this.zipValidated) {
          this.onZipValidated();
        }
      }).observe(line, { attributes: true, attributeFilter: ['class'] });

      // Handle race: autoCheckZipFromUrl may have already set is-delivarable
      // before this observer was registered
      if (line.classList.contains('is-delivarable') && !this.zipValidated) {
        this.onZipValidated();
      }
    },

    activeTab() {
      return this.el.querySelector('.js-variant-tab[aria-pressed="true"]')?.dataset.tab ?? 'exchange';
    },

    onZipValidated() {
      this.zipValidated = true;
      window._subBuyboxZipValidated = true;

      // Validate both tabs — zip is valid regardless of which tab is active
      this.qs('.js-spare-check-avail')?.classList.add('hidden');
      const spareBtn = this.qs('.js-spare-add-to-cart');
      spareBtn?.classList.remove('hidden');
      spareBtn?.classList.add('flex');

      this.qs('.js-sub-check-avail')?.classList.add('hidden');
      const subBtn = this.qs('.js-sub-add-to-cart');
      subBtn?.classList.remove('hidden');
      subBtn?.classList.add('flex');

      this.updateUI();
    },

    resetDeliverability() {
      this.zipValidated = false;
      window._subBuyboxZipValidated = false;

      this.qs('.js-sub-check-avail')?.classList.remove('hidden');
      const exchBtn = this.qs('.js-sub-add-to-cart');
      exchBtn?.classList.add('hidden');
      exchBtn?.classList.remove('flex');

      this.qs('.js-spare-check-avail')?.classList.remove('hidden');
      const spareBtn = this.qs('.js-spare-add-to-cart');
      spareBtn?.classList.add('hidden');
      spareBtn?.classList.remove('flex');

      const line = this.qs('.js-deliveringzip');
      line?.classList.remove('is-delivarable');
      line?.classList.add('hidden');
      line?.classList.remove('flex');
    },

    setQty(n) {
      this.qty = Math.min(18, Math.max(1, n));
      this.updateUI();
    },

    getTier() {
      const totalQty = this.qty + this.existingSubQty;
      return TIERS.find(t => totalQty >= t.min && totalQty <= t.max) ?? TIERS[0];
    },

    // Matches Recharge's per-item rounding: plan discount rounded, then tiered floored
    calcUnitCents(tier) {
      const planPrice = Math.round(this.basePrice * (1 - PLAN_DISCOUNT));
      const tieredDisc = Math.floor(planPrice * tier.tiered / 100);
      return planPrice - tieredDisc;
    },

    money(cents) {
      const val = (cents / 100).toFixed(2);
      return '$' + (val.endsWith('.00') ? val.slice(0, -3) : val);
    },

    updateUI() {
      const isSubscribe = PlanSelector.selected !== 'otp';
      const tier        = isSubscribe ? this.getTier() : { min: 1, max: 2, discount: 0, tiered: 0 };
      const unitCents   = isSubscribe
        ? this.calcUnitCents(tier)
        : this.basePrice;
      const totalCents  = unitCents * this.qty;

      // Hide entire tier bar when OTP selected
      this.qs('.js-tier-wrap')?.classList.toggle('hidden', !isSubscribe);

      const set = (sel, val) => { const el = this.qs(sel); if (el) el.textContent = val; };
      set('.js-sub-qty',        this.qty);
      set('.js-sub-unit-price', `${this.money(unitCents)}`);
      set('.js-sub-btn-total',  `— ${this.money(totalCents)}`);

      // Top price badge
      const badge = this.qs('.js-sub-discount-badge');
      if (badge) {
        badge.textContent = `${tier.discount}% off`;
        badge.classList.toggle('hidden', !isSubscribe || tier.discount === 0);
      }

      // Top strikethrough
      const compareEl = this.qs('.js-sub-compare-price');
      if (compareEl) {
        compareEl.textContent = this.money(this.basePrice);
        compareEl.classList.toggle('hidden', !isSubscribe || tier.discount === 0);
      }

      // Plan card prices — Subscribe card always shows discounted price (savings incentive)
      const subTier      = this.getTier();
      const subUnitCents = this.calcUnitCents(subTier);
      const planSubPrice = this.qs('.js-plan-sub-price');
      const planCompare  = this.qs('.js-plan-compare');
      const planOtpPrice = this.qs('.js-plan-otp-price');
      if (planSubPrice) planSubPrice.textContent = this.money(subUnitCents);
      if (planCompare) {
        planCompare.textContent = this.money(this.basePrice);
        planCompare.classList.toggle('hidden', subTier.discount === 0);
      }
      if (planOtpPrice) planOtpPrice.textContent = this.money(this.basePrice);

      // Qty stepper buttons
      const minusBtn = this.qs('.js-sub-qty-minus');
      const plusBtn  = this.qs('.js-sub-qty-plus');
      if (minusBtn) minusBtn.disabled = this.qty <= 1;
      if (plusBtn) {
        plusBtn.disabled = this.qty >= 18;
        plusBtn.classList.toggle('opacity-30', this.qty >= 18);
      }

      // Tier pills — only highlight active tier when subscribe is selected
      const subTotalQty = this.qty + this.existingSubQty;
      const pills  = this.qsAll('.js-sub-tier[data-min]');
      const labels = this.qsAll('.js-sub-tier-label');
      pills.forEach((pill, i) => {
        const isActive = isSubscribe && subTotalQty >= parseInt(pill.dataset.min, 10) && subTotalQty <= parseInt(pill.dataset.max, 10);
        pill.style.backgroundColor = isActive ? '#3f9cd2' : '#cce4f6';
        pill.style.color           = isActive ? '#ffffff' : '#3f9cd2';
        if (labels[i]) {
          labels[i].classList.toggle('font-bold',   isActive);
          labels[i].classList.toggle('font-book', !isActive);
        }
      });

      // Progress bar fills — frozen at 0 when OTP selected
      const fill1 = this.qs('.js-sub-tier-fill-1');
      const fill2 = this.qs('.js-sub-tier-fill-2');
      let pct1 = 0, pct2 = 0;
      if (isSubscribe) {
        if (tier.min === 1) {
          pct1 = ((subTotalQty - 1) / 2) * 100;
        } else if (tier.min === 3) {
          pct1 = 100;
          pct2 = ((subTotalQty - 3) / 3) * 100;
        } else {
          pct1 = 100;
          pct2 = 100;
        }
      }
      if (fill1) fill1.style.width = `${Math.max(Math.min(pct1, 100), isSubscribe ? 10 : 0)}%`;
      if (fill2) fill2.style.width = `${Math.min(pct2, 100)}%`;
    },

    async fetchAndPopulatePlans() {
      const handle = this.el.dataset.productHandle;
      if (!handle) return;

      try {
        const res     = await fetch(`/products/${handle}.js`);
        const product = await res.json();

        // Build plan ID → name map from selling_plan_groups
        const planNames = {};
        (product.selling_plan_groups || []).forEach(group => {
          (group.selling_plans || []).forEach(plan => {
            planNames[plan.id] = plan.name;
          });
        });

        // Find exchange variant by SKU or fall back to first
        const variant = (product.variants || []).find(v => v.sku === 'GX') || product.variants?.[0];
        const allocations = variant?.selling_plan_allocations || [];
        if (!allocations.length) return;

        const select = this.qs('.js-sub-frequency');
        if (!select) return;

        // Populate dropdown with unique plans
        const seen = new Set();
        allocations.forEach(alloc => {
          if (seen.has(alloc.selling_plan_id)) return;
          seen.add(alloc.selling_plan_id);
          const opt = document.createElement('option');
          opt.value       = alloc.selling_plan_id;
          opt.textContent = planNames[alloc.selling_plan_id] || `Plan ${alloc.selling_plan_id}`;
          select.appendChild(opt);
        });

        // Default to a 30-day plan if available, otherwise first option
        let defaultId = parseInt(select.options[0]?.value, 10);
        Array.from(select.options).forEach(opt => {
          if (opt.textContent.includes('30')) {
            defaultId = parseInt(opt.value, 10);
            select.value = opt.value;
          }
        });

        this.sellingPlanId = defaultId;
        // Sync visible display text
        const display = this.qs('.js-freq-display');
        if (display) display.textContent = select.options[select.selectedIndex]?.text || '';
        this.updateUI();
      } catch (err) {
        console.error('[SubBuybox] fetchAndPopulatePlans:', err);
      }
    },

    toggleDescription() {
      const btn = this.qs('.js-sub-read-more');
      if (!btn) return;
      const wrap = btn.closest('.js-sub-desc');
      const expanded = !wrap?.classList.contains('is-expanded');
      wrap?.classList.toggle('is-expanded', expanded);
      wrap?.querySelector('.js-sub-desc-truncated')?.classList.toggle('hidden', expanded);
      wrap?.querySelector('.js-sub-desc-full')?.classList.toggle('hidden', !expanded);
      btn.textContent = expanded ? 'Read Less' : 'Read More';
      btn.setAttribute('aria-expanded', String(expanded));
    },

    async addToCart() {
      const btn    = this.qs('.js-sub-add-to-cart');
      const label  = this.qs('.js-sub-btn-text');
      const total  = this.qs('.js-sub-btn-total');
      const loader = this.qs('.js-sub-btn-loader');

      if (!btn || btn.disabled) return;
      btn.disabled = true;
      label?.classList.add('hidden');
      total?.classList.add('hidden');
      loader?.classList.remove('hidden');

      try {
        const safeQty = Math.min(18, Math.max(1, this.qty));
        const item = { id: this.variantId, quantity: safeQty };
        if (PlanSelector.selected !== 'otp') item.selling_plan = this.sellingPlanId;

        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });

        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description ?? 'Add to cart failed');

        // Re-sync existingSubQty so tier bar reflects the newly added items
        await this.fetchExistingSubQty();
        this.updateUI();
        SpareBuybox.updateUI();
      } catch (err) {
        console.error('[SubBuybox]', err.message);
      } finally {
        btn.disabled = false;
        label?.classList.remove('hidden');
        total?.classList.remove('hidden');
        loader?.classList.add('hidden');
      }
    },
  };

  document.addEventListener('DOMContentLoaded', () => SubBuybox.init());
})();
